<script setup>
// One conversation in the expanded (full-width) list: a row with the Polaris kit's table shape
// (the classes of `next/polaris/ui/table`'s TableRow/TableCell) over the woot's columns. The list
// is virtual (rows are plain `div`s, not a `<table>`), so only the kit's classes are borrowed here.
// The root stays `div.conversation` (`.active` when open, `.selected` when picked for a bulk
// action): the list's keyboard shortcuts and the resolve action find rows by those classes.
import { computed, useTemplateRef } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import CardAvatar from './CardAvatar.vue';
import CardContent from './CardContent.vue';
import CardLabels from './CardLabelsV5.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';
import InboxName from 'dashboard/components-next/Conversation/InboxName.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import SLACardLabel from 'dashboard/components-next/Conversation/Sla/SLACardLabel.vue';
import CardStatusIcon from './CardStatusIcon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  isInboxView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'selectConversation',
  'deSelectConversation',
  'click',
  'contextmenu',
]);

const lastMessageInChat = computed(() => getLastMessage(props.chat));
const showLabelsSection = computed(() => props.chat.labels?.length > 0);

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const unreadCount = computed(() => props.chat.unread_count);

const slaCardLabel = useTemplateRef('slaCardLabel');

const hasSlaPolicyId = computed(
  () =>
    !props.currentContact?.blocked &&
    (props.chat?.applied_sla?.id || slaCardLabel.value?.hasSlaThreshold)
);

const selectedModel = computed({
  get: () => props.selected,
  set: value => {
    if (value) {
      emit('selectConversation', value);
    } else {
      emit('deSelectConversation', value);
    }
  },
});
</script>

<template>
  <div
    class="conversation grid cursor-pointer items-center gap-4 border-b border-solid border-polaris-border px-3 h-12 type-polaris-body text-polaris-foreground transition-colors hover:bg-polaris-muted/50 data-[state=selected]:bg-polaris-muted"
    :class="{
      active: isActiveChat,
      selected,
      'grid-cols-[minmax(0,2fr)_minmax(0,1fr)]': showLabelsSection,
      'grid-cols-[minmax(0,2fr)_max-content]': !showLabelsSection,
    }"
    :data-state="isActiveChat || selected ? 'selected' : undefined"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <!-- LEFT SECTION -->
    <div class="flex min-w-0 flex-1 items-center">
      <div
        class="flex w-8 flex-shrink-0 items-center justify-center p-2"
        @click.stop
      >
        <Checkbox v-model="selectedModel" />
      </div>

      <div class="flex w-8 flex-shrink-0 items-center justify-center p-2">
        <CardPriorityIcon :priority="chat.priority" show-empty />
      </div>

      <div class="flex w-8 flex-shrink-0 items-center justify-center p-2">
        <Avatar
          v-if="showAssignee && assignee.name"
          v-tooltip.top="{
            content: assignee.name,
            delay: { show: 500, hide: 0 },
          }"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="14"
          :status="assignee.availability_status"
          hide-offline-status
        />
        <Icon
          v-else
          icon="i-woot-empty-assignee"
          class="size-4 text-polaris-muted-foreground"
        />
      </div>

      <div class="flex w-8 flex-shrink-0 items-center justify-center p-2">
        <CardStatusIcon :status="chat.status" show-empty />
      </div>

      <div
        v-if="!isInboxView && showInboxName"
        class="w-20 flex-shrink-0 whitespace-nowrap p-2"
      >
        <InboxName :inbox="inbox" class="min-w-0" />
      </div>

      <div
        v-tooltip.top="{
          content: chat.id,
          delay: { show: 500, hide: 0 },
        }"
        class="flex h-6 w-full max-w-20 min-w-0 flex-shrink-0 items-center gap-1 whitespace-nowrap p-2"
      >
        <Icon
          icon="i-woot-hash"
          class="size-3.5 flex-shrink-0 text-polaris-muted-foreground"
        />
        <span class="truncate text-polaris-muted-foreground">
          {{ chat.id }}
        </span>
      </div>

      <div class="flex-shrink-0 p-2">
        <CardAvatar
          :contact="currentContact"
          :selected="false"
          :enable-selection="false"
          :hide-thumbnail="false"
        />
      </div>

      <div class="w-32 flex-shrink-0 whitespace-nowrap p-2">
        <h4 class="my-0 truncate type-polaris-body font-medium capitalize">
          {{ currentContact.name }}
        </h4>
      </div>

      <div class="min-w-0 flex-1 truncate p-2">
        <CardContent
          :last-message="lastMessageInChat"
          :voice-call-status="voiceCallData.status"
          :voice-call-direction="voiceCallData.direction"
          :unread-count="unreadCount"
          :show-expanded-preview="false"
        />
      </div>
    </div>

    <!-- RIGHT SECTION -->
    <div class="flex flex-shrink-0 items-center justify-end">
      <div v-if="showLabelsSection" class="w-full min-w-0 p-2">
        <CardLabels
          :labels="chat.labels"
          disable-toggle
          class="my-0 justify-end [&>div]:justify-end"
        />
      </div>

      <div v-if="hasSlaPolicyId" class="flex-shrink-0 whitespace-nowrap p-2">
        <SLACardLabel ref="slaCardLabel" :chat="chat" />
      </div>

      <div class="w-[4.375rem] flex-shrink-0 whitespace-nowrap p-2 text-end">
        <TimeAgo
          :conversation-id="chat.id"
          :last-activity-timestamp="chat.timestamp"
          :created-at-timestamp="chat.created_at"
          class="!text-xs !text-polaris-muted-foreground"
        />
      </div>
    </div>
  </div>
</template>
